import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import Badge from '@mui/material/Badge';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Link, useNavigate } from 'react-router-dom';

import WebstoreServiceModal from './WebstoreServiceModal';
import WebstoreCartModal from './WebstoreCartModal';
import WebstoreUserOrders from './WebstoreUserOrders';
import WebstoreUserChat from './WebstoreUserChat';

import Helmet from 'react-helmet';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const Webstore = () => {
  const [user, setUser] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [activeTab, setActiveTab] = useState('shop');
  const [cooldownInfo, setCooldownInfo] = useState(null);

  const navigate = useNavigate();

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await axios.get(`${API_BASE_URL}/api/webstore-users/userinfo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (err) {
      console.error('Unauthorized or token expired', err);
      localStorage.removeItem('token');
    }
  };

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/webstore-services`);
      // Sort services by price (lowest to highest)
      const sortedServices = res.data.sort((a, b) => a.price - b.price);
      setServices(sortedServices);
    } catch (err) {
      console.error('Failed to fetch services', err);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchCooldownInfo = async () => {
      if (!user) return;
      try {
        const res = await axios.get(`${API_BASE_URL}/api/webstore-orders/cooldown?email=${user.email}`);
        setCooldownInfo(res.data);
      } catch (err) {
        console.error('Failed to fetch cooldown info', err);
        setCooldownInfo(null); // Reset cooldown info on error
      }
    };

    fetchCooldownInfo();
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/webstore-login');
  };

  const handleAddToCart = (service) => {
    if (!user) {
      navigate('/webstore-login');
      return;
    }

    // Check if the user is in a cooldown period or has reached the order limit
    // Ensure cooldownInfo is available and valid before checking limits
    if (cooldownInfo && cooldownInfo.onCooldown && cooldownInfo.remainingOrders <= 0) {
      // You might want to show a toast message here to inform the user
      // Example: toast.info('You have reached your order limit. Please try again later.');
      console.log('Cannot add to cart: Cooldown active and no remaining orders.');
      return;
    }

    if (cooldownInfo && cartItems.length >= cooldownInfo.remainingOrders) {
      // Show a warning if adding this item would exceed the limit
      // Example: toast.warn(`You can only add ${cooldownInfo.remainingOrders} item(s) to your cart.`);
      console.log('Cannot add to cart: Cart limit reached.');
      return;
    }

    setCartItems((prev) => [...prev, service]);
  };

  const handleRemoveFromCart = (indexToRemove) => {
    setCartItems((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleClearCart = () => {
    setCartItems([]);
  };

  return (
    <div className="webstore-wrapper">
      <Helmet>
        <title>NavarroJalen | Webstore</title>
        <meta name="description" content="NavarroJalen | Webstore" />
        <meta property="og:title" content="NavarroJalen | Webstore" />
      </Helmet>
      <header className="webstore-header">
        <Link to="/portfolio" className="webstore-title">Webstore</Link>

        <nav className="desktop-nav">
          {user ? (
            <>
              <div className="user-info">
                <span className="username">Hello, {user.username}</span>
                <span className="credits">NJcredits: {user.njCredits}</span>
              </div>
              <Badge badgeContent={cartItems.length} color="primary" className="cart-badge">
                <ShoppingCartIcon
                  className="cart-icon"
                  onClick={() => setShowCartModal(true)}
                />
              </Badge>
              <button className="logout-btn" onClick={handleLogout}>Logout</button>
            </>
          ) : (
            // <button className="login-btn" onClick={() => navigate('/webstore-login')}>
            //   Login
            // </button>
             <button className="login-btn" onClick={() => navigate('')}>
            
            </button>
          )}
        </nav>

        <div className="hamburger" onClick={() => setMenuOpen((prev) => !prev)}>
          {menuOpen ? <CloseIcon /> : <MoreHorizIcon />}
        </div>

        <div className={`side-menu ${menuOpen ? 'open' : ''}`}>
          <div className="side-menu-header">
            <span className="side-menu-title">Menu</span>
            <CloseIcon className="side-menu-close" onClick={() => setMenuOpen(false)} />
          </div>
          {user ? (
            <div className="side-menu-content">
              <div className="user-info-mobile">
                <span>Hello, {user.username}</span>
                <span className="credits">NJcredits: {user.njCredits}</span>
              </div>
              <button
                className={`side-menu-tab-btn ${activeTab === 'shop' ? 'active' : ''}`}
                onClick={() => { setActiveTab('shop'); setMenuOpen(false); }}
              >
                Shop
              </button>
              <button
                className={`side-menu-tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => { setActiveTab('orders'); setMenuOpen(false); }}
              >
                My Orders
              </button>
              <button
                className={`side-menu-tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => { setActiveTab('chat'); setMenuOpen(false); }}
              >
                Chat
              </button>
              <div className="side-menu-cart-logout">
                <Badge badgeContent={cartItems.length} color="primary">
                  <ShoppingCartIcon
                    className="cart-icon-mobile"
                    onClick={() => { setShowCartModal(true); setMenuOpen(false); }}
                  />
                </Badge>
                <button className="logout-btn-mobile" onClick={handleLogout}>Logout</button>
              </div>
            </div>
          ) : (
            <button className="login-btn-mobile" onClick={() => navigate('/webstore-login')}>
              Login
            </button>
            
          )}
        </div>
      </header>

      {user && (
        <div className="customer-tabs">
          <button
            className={activeTab === 'shop' ? 'active' : ''}
            onClick={() => setActiveTab('shop')}
          >
            Shop
          </button>
          <button
            className={activeTab === 'orders' ? 'active' : ''}
            onClick={() => setActiveTab('orders')}
          >
            My Orders
          </button>
          <button
            className={activeTab === 'chat' ? 'active' : ''}
            onClick={() => setActiveTab('chat')}
          >
            Chat
          </button>
        </div>
      )}

      <main className="webstore-content">
        {activeTab === 'shop' && (
          <>
            {cooldownInfo && cooldownInfo.onCooldown && cooldownInfo.nextOrderAvailableAt && (
              <p className="cart-limit-warning">
                You are currently on a cooldown. Next order available:{' '}
                <strong>{new Date(cooldownInfo.nextOrderAvailableAt).toLocaleString()}</strong>
              </p>
            )}
            {cooldownInfo && !cooldownInfo.onCooldown && cooldownInfo.remainingOrders !== null && (
               <p className="cart-limit-warning">
                 You can add up to <strong>{cooldownInfo.remainingOrders}</strong> more item{cooldownInfo.remainingOrders !== 1 ? 's' : ''} to your cart.
               </p>
            )}
            <div className="product-grid">
              {services.length === 0 ? (
                <p>No services available.</p>
              ) : (
                services.map((service) => {
                  const isLimitReached = cooldownInfo && cooldownInfo.remainingOrders !== null && cartItems.length >= cooldownInfo.remainingOrders;
                  const isCooldownActiveAndLimitZero = cooldownInfo && cooldownInfo.onCooldown && cooldownInfo.remainingOrders <= 0;

                  return (
                    <div key={service._id} className="service-card">
                      {service.images?.[0] && (
                        <img
                          src={service.images[0]}
                          alt={service.title}
                          className="service-image"
                          onClick={() => setSelectedService(service)}
                        />
                      )}
                      <h3>{service.title}</h3>
                      <p className="category">{service.category}</p>
                      <p className="description">{service.description}</p>
                      <p className="price">
                        {/* Modified for robust PHP currency display */}
                        <small>
                          {service.category} | ₱
                          {new Intl.NumberFormat('en-PH', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(service.price)}
                        </small>
                      </p>
                      <p className="delivery">Delivery within {service.deliveryTime || 3} days</p>
                      <div className="service-actions">
                        <button className="view-btn" onClick={() => setSelectedService(service)}>
                          View
                        </button>
                        {user ? (
                          <button
                            className="cart-btn"
                            onClick={() => handleAddToCart(service)}
                            disabled={isLimitReached || isCooldownActiveAndLimitZero} // Disable if limit reached or cooldown active with 0 remaining
                          >
                            {isCooldownActiveAndLimitZero ? 'Cooldown Active' : (isLimitReached ? 'Limit Reached' : 'Add to Cart')}
                          </button>
                        ) : (
                          // <Link to="/webstore-login" className="cart-btn link-btn">
                          //   Login to Add
                          // </Link>
                          <Link to="" className="cart-btn link-btn">                           
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {activeTab === 'orders' && user && (
          <WebstoreUserOrders user={user} />
        )}

        {activeTab === 'chat' && user && (
          <WebstoreUserChat user={user} />
        )}
      </main>

      {selectedService && (
        <WebstoreServiceModal
          service={selectedService}
          user={user}
          onClose={() => setSelectedService(null)}
        />
      )}

      {showCartModal && (
        <WebstoreCartModal
          items={cartItems}
          user={user}
          onRemove={handleRemoveFromCart}
          onClose={() => setShowCartModal(false)}
          onClearCart={handleClearCart}
          onUserUpdate={setUser}
          fetchUser={fetchUser}
          fetchCooldownInfo={() => {
            if (user) {
              axios.get(`${API_BASE_URL}/api/webstore-orders/cooldown?email=${user.email}`)
                .then(res => setCooldownInfo(res.data))
                .catch(err => console.error('Failed to re-fetch cooldown info', err));
            }
          }}
        />
      )}
    </div>
  );
};

export default Webstore;